import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { attachSupplierPdf } from "@/lib/adminSupplierInvoicesApi";

interface Props {
  accessKey: string;
  number: string | null;
  supplier: string | null;
  replace: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAttached: () => void;
}

/**
 * Anexa (ou substitui) o PDF ORIGINAL do fornecedor. Antes de concluir, mostra a identidade da NF
 * (número, fornecedor, chave) e exige: selecionar o arquivo, poder CONFERI-LO (abre em nova aba) e
 * CONFIRMAR que corresponde à NF. O backend valida formato/estrutura do PDF e o tamanho.
 */
export function SupplierInvoicePdfAttachDialog({
  accessKey, number, supplier, replace, open, onOpenChange, onAttached,
}: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setFile(null);
      setConfirmed(false);
      setBusy(false);
    }
  }, [open]);

  const previewLocal = () => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const submit = async () => {
    if (!file || !confirmed) return;
    setBusy(true);
    try {
      await attachSupplierPdf(accessKey, file, replace);
      toast.success(replace ? "PDF do fornecedor substituído." : "PDF do fornecedor anexado.");
      onAttached();
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha ao anexar o PDF.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{replace ? "Substituir PDF do fornecedor" : "Anexar PDF do fornecedor"}</DialogTitle>
          <DialogDescription>
            Envie o PDF original recebido do fornecedor. Ele é documental: não altera o XML, dados fiscais,
            mapeamentos ou custos.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="rounded border border-border bg-muted/40 p-2 text-xs">
            <div className="font-medium">Confira se o arquivo corresponde a esta NF:</div>
            <div>Número: <strong>{number ?? "—"}</strong></div>
            <div>Fornecedor: <strong>{supplier ?? "—"}</strong></div>
            <div className="break-all">Chave de acesso: <strong>{accessKey}</strong></div>
          </div>

          <input
            type="file"
            accept="application/pdf,.pdf"
            onChange={(e) => { setFile(e.target.files?.[0] ?? null); setConfirmed(false); }}
            aria-label="Selecionar PDF do fornecedor"
          />

          {file && (
            <div className="flex items-center justify-between gap-2 text-xs">
              <span className="truncate">Selecionado: <strong>{file.name}</strong> ({Math.round(file.size / 1024)} KB)</span>
              <Button size="sm" variant="outline" type="button" onClick={previewLocal}>Conferir arquivo</Button>
            </div>
          )}

          {replace && (
            <div className="rounded border border-warning/40 bg-warning/10 p-2 text-xs text-warning">
              Substituição: a versão atual é preservada no histórico (não é sobrescrita).
            </div>
          )}

          <label className="flex items-start gap-2 text-xs">
            <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} disabled={!file} />
            Confirmo que este arquivo é o PDF do fornecedor correspondente à NF acima.
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancelar</Button>
          <Button onClick={() => void submit()} disabled={!file || !confirmed || busy}>
            {replace ? "Substituir" : "Anexar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
